import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import axios from "axios";
import { API } from "../lib/api";
import { StoryRender, ShareButtons } from "../components/story/StoryShared";

/* Public / unlisted Travel Story page — no login needed. */
export default function PublicStory() {
  const { publicId } = useParams();
  const [view, setView] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    axios.get(`${API}/stories/public/${publicId}`).then((r) => {
      setView(r.data);
      document.title = `${r.data.title} — TravelerPulse.pro`;
    }).catch(() => setErr(true));
  }, [publicId]);

  if (err) return (
    <div className="max-w-2xl mx-auto px-5 py-24 text-center" data-testid="public-story-notfound">
      <h1 className="font-heading font-900 text-2xl mb-2">This story isn't available</h1>
      <p className="text-sm text-muted-foreground">It may have been made private or removed by its author.</p>
      <Link to="/" className="inline-block mt-4 text-primary hover:underline text-sm">Explore TravelerPulse.pro →</Link>
    </div>
  );
  if (!view) return <div className="max-w-3xl mx-auto px-5 py-16 text-sm text-muted-foreground">Loading story…</div>;

  const url = `${window.location.origin}/story/${view.public_id}`;
  const cardUrl = `${API}/stories/share/${view.public_id}`;
  return (
    <div className="px-5 lg:px-8 py-10" data-testid="public-story">
      <StoryRender view={view} />
      <div className="max-w-3xl mx-auto mt-8">
        <div className="text-sm font-700 mb-2">Share this story</div>
        <ShareButtons url={url} title={view.title} cardUrl={cardUrl} publicId={view.public_id} />
        {view.visibility === "unlisted" && (
          <p className="text-[11px] text-muted-foreground mt-3 inline-flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" /> Unlisted story — only people with this link can view it.</p>
        )}
        {view.comments_mode !== "off" && <StoryComments publicId={view.public_id} mode={view.comments_mode} />}
      </div>
    </div>
  );
}

function StoryComments({ publicId, mode }) {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [text, setText] = useState("");
  const [sent, setSent] = useState(false);
  const signedIn = Boolean(localStorage.getItem("token"));
  useEffect(() => {
    axios.get(`${API}/stories/public/${publicId}/comments`).then((r) => setItems(r.data.items)).catch(() => {});
  }, [publicId]);

  const submit = async () => {
    if (!text.trim()) return;
    try {
      const headers = signedIn ? { Authorization: `Bearer ${localStorage.getItem("token")}` } : {};
      await axios.post(`${API}/stories/public/${publicId}/comments`, { name, text }, { headers });
      setSent(true); setText("");
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not send your comment");
    }
  };

  return (
    <section className="mt-10 border-t border-zinc-200 pt-6" data-testid="story-comments">
      <h2 className="font-heading font-900 text-lg mb-3">Comments {items.length > 0 && <span className="text-sm text-muted-foreground font-400">({items.length})</span>}</h2>
      {items.map((c) => (
        <div key={c.id} className="border border-zinc-200 bg-white px-3 py-2.5 mb-2" data-testid={`story-comment-${c.id}`}>
          <div className="text-xs font-700">{c.name} <span className="text-muted-foreground font-400">· {(c.created_at || "").slice(0, 10)}</span></div>
          <p className="text-sm mt-0.5 whitespace-pre-line">{c.text}</p>
        </div>
      ))}
      {items.length === 0 && <p className="text-sm text-muted-foreground mb-3">No comments yet — be the first.</p>}
      {sent ? (
        <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-2" data-testid="story-comment-sent">Thanks! Your comment is awaiting the author's approval.</p>
      ) : mode === "members" && !signedIn ? (
        <p className="text-sm text-muted-foreground border border-dashed border-zinc-300 px-3 py-2.5">
          Comments are open to TravelerPulse members — <Link to="/login" className="text-primary hover:underline">sign in</Link> to join the conversation.
        </p>
      ) : (
        <div className="space-y-2">
          {!signedIn && (
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" maxLength={60} data-testid="story-comment-name"
              className="w-full max-w-xs border border-zinc-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary bg-white" />
          )}
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} maxLength={1000} placeholder="Share your thoughts about this story…" data-testid="story-comment-text"
            className="w-full border border-zinc-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary bg-white" />
          <button onClick={submit} data-testid="story-comment-submit" className="px-4 py-2 bg-indigo-600 text-white text-sm font-700">Post comment</button>
          <p className="text-[11px] text-muted-foreground">Comments appear after the story's author approves them.</p>
        </div>
      )}
    </section>
  );
}
